import { defaultLocale, localeFromPathname, localizePath, supportedLocales, type Locale } from '@shared/locales';
import {
  getContactContent,
  getFaqContent,
  getGetStartedContent,
  getHomeContent,
  getProductContent,
  getSettings,
  paragraphs,
} from './lib/content';
import { buildConfig } from './lib/config';
import i18n from './i18n';
import { getDocs, getLegalPage } from './lib/docs';
import { legalSlugs } from '@shared/content-schemas';

/**
 * The route inventory: every page the prerenderer writes, in every locale,
 * with the metadata its head needs. The sitemap, the Markdown twins and the
 * route tests are all built from this one list.
 */
export interface RouteMeta {
  /** The localized path, e.g. `/product` or `/es/product`. */
  path: string;
  title: string;
  description: string;
  noindex?: boolean;
  /** YYYY-MM-DD, only where the content carries a real date. */
  lastmod?: string;
  jsonLd?: Record<string, unknown>;
}

/** Unprefixed marketing page paths, in navigation order. Docs and legal pages are added from content. */
export const PAGE_PATHS = ['/', '/product', '/get-started', '/faq', '/contact'] as const;

function organizationJsonLd(locale: Locale) {
  const site = buildConfig.siteUrl;
  const settings = getSettings(locale);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${site}/#organization`,
        name: settings.siteName,
        url: `${site}/`,
        logo: `${site}/images/logo-mark.png`,
      },
      {
        '@type': 'WebSite',
        '@id': `${site}/#website`,
        name: settings.siteName,
        url: `${site}${localizePath('/', locale) === '/' ? '/' : localizePath('/', locale)}`,
        inLanguage: locale,
        publisher: { '@id': `${site}/#organization` },
      },
    ],
  };
}

function faqJsonLd(locale: Locale) {
  const faq = getFaqContent(locale);
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: locale,
    mainEntity: faq.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: paragraphs(item.answer).join('\n\n') },
    })),
  };
}

export function allRoutes(locale: Locale = defaultLocale): RouteMeta[] {
  const at = (path: string) => localizePath(path, locale);
  const home = getHomeContent(locale);
  const product = getProductContent(locale);
  const getStarted = getGetStartedContent(locale);
  const faq = getFaqContent(locale);
  const contact = getContactContent(locale);
  return [
    { path: at('/'), ...home.meta, jsonLd: organizationJsonLd(locale) },
    { path: at('/product'), ...product.meta },
    { path: at('/get-started'), ...getStarted.meta },
    { path: at('/faq'), ...faq.meta, jsonLd: faqJsonLd(locale) },
    { path: at('/contact'), ...contact.meta },
    {
      path: at('/docs'),
      title: i18n.getFixedT(locale)('docs.title'),
      description: i18n.getFixedT(locale)('docs.description'),
    },
    ...getDocs(locale).map((doc) => ({
      path: at(`/docs/${doc.slug}`),
      title: doc.title,
      description: doc.description,
      lastmod: doc.updated,
    })),
    ...legalSlugs.flatMap((slug) => {
      const page = getLegalPage(slug, locale);
      if (!page) return [];
      // Drafts are published so they can be reviewed, but kept out of search
      // indexes and the sitemap until the owner approves them.
      return [
        {
          path: at(`/${slug}`),
          title: page.title,
          description: page.description,
          lastmod: page.updated,
          noindex: page.status === 'draft',
        },
      ];
    }),
  ];
}

export function allLocalizedRoutes(): RouteMeta[] {
  return supportedLocales.flatMap((locale) => allRoutes(locale));
}

export function routeMetaFor(pathname: string): RouteMeta | undefined {
  const { locale, pathname: unprefixed } = localeFromPathname(pathname);
  return allRoutes(locale).find((route) => localeFromPathname(route.path).pathname === unprefixed);
}

/** The 404 page's metadata; prerendered once per locale, always noindex. */
export function notFoundMeta(locale: Locale): RouteMeta {
  const t = i18n.getFixedT(locale);
  return {
    path: localizePath('/404', locale),
    title: t('notFound.title'),
    description: t('notFound.description'),
    noindex: true,
  };
}
